"use client";

import { Skeleton } from "@frontend/shared/components/ui/skeleton";
import { cn } from "@frontend/shared/lib/utils";
import { type ReactNode, createContext, forwardRef, useContext } from "react";
import { createPortal } from "react-dom";

// Context for sharing isLoading state
interface KioskCardContextValue {
  isLoading?: boolean;
}

const KioskCardContext = createContext<KioskCardContextValue>({});

const useKioskCard = () => useContext(KioskCardContext);

interface KioskCardProps extends React.HTMLAttributes<HTMLDivElement> {
  isLoading?: boolean;
  variant?: "default" | "elevated" | "bordered";
  useThreeJsGlass?: boolean; // Keep for compatibility even though unused
}

export const KioskCard = forwardRef<HTMLDivElement, KioskCardProps>(
  (
    { children, isLoading = false, variant = "default", className, ...props },
    ref,
  ) => {
    return (
      <KioskCardContext.Provider value={{ isLoading }}>
        <div
          ref={ref}
          className={cn(
            "relative w-full max-w-2xl flex flex-col items-center gap-4 md:gap-9",
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </KioskCardContext.Provider>
    );
  },
);

KioskCard.displayName = "KioskCard";

// Title Component
interface KioskCardTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const KioskCardTitle = forwardRef<HTMLDivElement, KioskCardTitleProps>(
  ({ children, className, ...props }, ref) => {
    const { isLoading } = useKioskCard();

    return (
      <div
        ref={ref}
        className={cn("space-y-3 md:space-y-2 text-center", className)}
        {...props}
      >
        {isLoading ? (
          <Skeleton className="h-8 w-3/4 mx-auto" />
        ) : (
          <h2 className="kiosk-h2-refactor">{children}</h2>
        )}
      </div>
    );
  },
);

KioskCardTitle.displayName = "KioskCardTitle";

// Content Component
interface KioskCardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const KioskCardContent = forwardRef<
  HTMLDivElement,
  KioskCardContentProps
>(({ children, className, ...props }, ref) => {
  const { isLoading } = useKioskCard();

  return (
    <div ref={ref} className={cn("w-full", className)} {...props}>
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </div>
      ) : (
        children
      )}
    </div>
  );
});

KioskCardContent.displayName = "KioskCardContent";

// Footer Component - renders buttons in a fixed bar at the bottom of the viewport
interface KioskCardFooterProps {
  children: ReactNode;
  className?: string;
}

export const KioskCardFooter = ({
  children,
  className,
}: KioskCardFooterProps) => {
  const { isLoading } = useKioskCard();

  // Portal to document.body so position: fixed works even when a parent
  // has a CSS transform (e.g. the kiosk-component-enter animation).
  return createPortal(
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between px-4 pb-[calc(16px+env(safe-area-inset-bottom))] md:px-12 md:pb-[50px] gap-3 md:gap-6",
        className,
      )}
    >
      {isLoading ? <Skeleton className="h-12 w-full sm:w-32" /> : children}
    </div>,
    document.body,
  );
};

KioskCardFooter.displayName = "KioskCardFooter";
